/**
 * TXM-594 — Alíquota interna / Método de cálculo da Tabela NCM: formatação, digitação e payload.
 *
 * Funções puras, sem DOM e sem HTTP (mesmo critério de parseCsvNcm.util.js), usadas pela
 * listagem e pelos dois modais. Regra de negócio (0–100, domínio do método, permissão de linha
 * global, conflito no mesmo escopo) é da API — aqui só se ajuda a digitar e se monta o payload.
 */

/**
 * Motivo exibido quando o item é (ou vai ficar) global e o usuário não tem
 * `ncm-table-edit-global`. Um texto só, usado no Novo Registro e no Editar.
 */
export const MOTIVO_SEM_PERMISSAO_GLOBAL =
  'Linha de "Todas as Empresas": alíquota e método valem para todos os clientes e só podem ser preenchidos ou alterados com a permissão de edição global da Tabela NCM.';

/** Hint do Editar numa linha global: por que NCM, Item e escopo estão travados (decisão 09-30). */
export const MOTIVO_ESCOPO_GLOBAL_IMUTAVEL =
  'Linha de "Todas as Empresas": vale para todos os clientes, por isso NCM, Item, Cliente, CNPJ e Estado não podem ser alterados.';

/** Linha global = sem grupo econômico ("Todas as Empresas"), com ou sem UF (decisão 09-30). */
export function ehLinhaGlobal(item) {
  const grupo = item?.id_grupo_economico;
  return grupo == null || String(grupo).trim() === "";
}

/** NCM digitado/colado com pontuação ("3002.42.00") vira só dígitos. Não trunca: um NCM com
 * dígitos a mais precisa aparecer como erro da API, não ser "consertado" em silêncio. */
export function sanitizarNcm(texto) {
  return String(texto ?? "").replace(/\D/g, "");
}

/**
 * Deixa passar só dígitos e UM separador decimal (vírgula ou ponto), até 3 dígitos inteiros e
 * 2 casas. Colar "18,00%" vira "18,00". O intervalo 0–100 continua sendo validado pela API.
 */
export function sanitizarAliquota(texto) {
  const limpo = String(texto ?? "").replace(/[^\d.,]/g, "");
  const posicaoSeparador = limpo.search(/[.,]/);
  if (posicaoSeparador < 0) return limpo.slice(0, 3);
  const inteiro = limpo.slice(0, posicaoSeparador).slice(0, 3);
  const decimais = limpo.slice(posicaoSeparador + 1).replace(/\D/g, "").slice(0, 2);
  return `${inteiro}${limpo[posicaoSeparador]}${decimais}`;
}

/** Valor gravado (number ou string "18.00" vinda do NUMERIC) → "18,00". null/vazio → "". */
export function aliquotaParaTexto(valor) {
  if (valor == null || String(valor).trim() === "") return "";
  const numero = Number(valor);
  if (Number.isNaN(numero)) return String(valor);
  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

/** Texto do formulário → número (aceita vírgula). "" → null; texto sem número → NaN. */
function aliquotaTextoParaNumero(texto) {
  const limpo = String(texto ?? "").trim();
  if (limpo === "") return null;
  return Number(limpo.replace(",", "."));
}

/** Valor do select: "" = herda. Linha legada com 'mva' minúsculo aparece como "MVA". */
export function normalizarMetodo(valor) {
  return valor == null ? "" : String(valor).trim().toUpperCase();
}

/** Rótulo da coluna "Método de cálculo". Valor fora do domínio aparece cru, sem ser escondido. */
export function rotuloMetodo(valor) {
  const metodo = normalizarMetodo(valor);
  if (metodo === "") return "Herda";
  if (metodo === "-") return "Não se aplica";
  return metodo;
}

/** Payload de criação: o que está no formulário, com vazio virando null (= herda). */
export function payloadAliquotaMetodo(form) {
  return {
    aliq_interna: String(form?.aliquota ?? "").trim() || null,
    metodo_calculo: form?.metodo || null,
  };
}

/**
 * NCM + escopo (Cliente, CNPJ, Estado) do PUT de edição.
 *
 * Linha global: NCM, Item e escopo são imutáveis (decisão 09-30), então NADA disso vai no PUT —
 * o PUT é parcial (campo ausente mantém o gravado). Mandar o mesmo valor "por garantia" faria a
 * API ter de distinguir "reenviou igual" de "tentou mudar"; não mandar elimina a dúvida.
 *
 * Linha de cliente: comportamento de sempre — o escopo vai inteiro (null em Cliente/Estado é uma
 * escolha válida, "Todas as Empresas"/"Todos os Estados", não "não mexer"). O NCM vai só com
 * dígitos (mesma regra que a API aplica antes de exigir ^[0-9]{8}$).
 */
export function payloadNcmEscopoEdicao(item, form) {
  if (ehLinhaGlobal(item)) return {};
  return {
    ncm: sanitizarNcm(form?.ncm),
    id_grupo_economico: form?.clienteNovo ? Number(form.clienteNovo.id) : null,
    cnpj: form?.clienteNovo ? form?.cnpjNovo ?? null : null,
    estado: form?.estadoNovo || null,
  };
}

/**
 * Payload de edição: só as colunas que mudaram. O PUT é parcial (campo ausente mantém o valor
 * gravado), então não mandar o que não mudou evita acionar a checagem de `ncm-table-edit-global`
 * numa linha global em que o usuário só mexeu na descrição, por exemplo.
 * 0 e vazio são diferentes: 0 é alíquota válida, vazio é "herda".
 *
 * Método: compara o valor CRU gravado com o do select (que já é canônico). Linha legada com
 * 'mva' minúsculo abre com "MVA" selecionado e conta como mudança mesmo sem o usuário tocar no
 * campo — a API recusa (METODO_CALCULO_INVALIDO) qualquer PUT nessa linha que não traga o
 * método explícito, porque o CHECK do banco não aceita mais 'mva' em escrita nova. Então o
 * primeiro save reenvia "MVA" e a linha fica canônica.
 */
export function payloadAliquotaMetodoAlterados(item, form) {
  const payload = {};

  const aliquotaGravada = item?.aliq_interna == null || String(item.aliq_interna).trim() === ""
    ? null
    : Number(item.aliq_interna);
  const aliquotaNova = aliquotaTextoParaNumero(form?.aliquota);
  // NaN nunca é igual a nada: texto inválido segue pra API, que devolve a mensagem de erro.
  if (aliquotaNova !== aliquotaGravada) {
    payload.aliq_interna = String(form?.aliquota ?? "").trim() || null;
  }

  const metodoGravadoCru = item?.metodo_calculo == null ? "" : String(item.metodo_calculo);
  const metodoNovo = normalizarMetodo(form?.metodo);
  if (metodoNovo !== metodoGravadoCru) {
    payload.metodo_calculo = metodoNovo || null;
  }

  return payload;
}
